import { Button, Space, Spin, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import _ from 'lodash';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import ILoan from '~Api/Loan/ILoan';
import { IDictionary } from '~utilities/IDictionary';
import ILoanDrawdown from '~Api/Loan/ILoanDrawdown';
import dayjs from 'dayjs';
import LoanDrawdownStatusEnum from '~Api/Loan/LoanDrawdownStatusEnum';
import Layout from './Layout';
import {
    loanDrawdownsListAction,
    loanGetAction,
} from '~Loans/actions';
import { IGlobalState } from '~reducer';
import {
    loanDrawdownsSelector,
    loanSelector,
} from '~Loans/selectors';
import ModalStateEnum from '~UI/ModalStateEnum';
import DrawdownAddModal from './DrawdownAddModal';
import LoanDrawdownStatusTag from './LoanDrawdownStatusTag';

export default function Drawdowns(): ReactElement {
    const { loanUuid } = useParams<{ loanUuid: string }>();

    const loan: ILoan = useSelector((state: IGlobalState) => loanSelector(state, loanUuid));
    const drawdowns: IDictionary<ILoanDrawdown> = useSelector((state: IGlobalState) => loanDrawdownsSelector(state, loanUuid));

    const [ isAddModalOpen, setIsAddModalOpen ] = useState<ModalStateEnum>(ModalStateEnum.Off);

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!loan) {
            dispatch(loanGetAction(loanUuid));
        }
    }, [
        dispatch,
        loan,
        loanUuid,
    ]);

    useEffect(() => {
        if (!drawdowns) {
            dispatch(loanDrawdownsListAction(loanUuid));
        }
    }, [
        dispatch,
        drawdowns,
        loanUuid,
    ]);

    const onClickCreate: () => void = useCallback(() => {
        setIsAddModalOpen(ModalStateEnum.Open);
    }, []);

    const onCloseAddModal: () => void = useCallback(() => {
        setIsAddModalOpen(ModalStateEnum.Closed);
    }, []);

    if (!loan || !drawdowns) {
        return (
            <Layout uuid={loanUuid} section='drawdowns'>
                <Typography.Title level={2}>Drawdowns</Typography.Title>
                <Spin />
            </Layout>
        );
    }

    const columns: ColumnsType<ILoanDrawdown> = [
        {
            dataIndex: 'drawdownNumber',
            render: (drawdownNumber: string, loanDrawdown: ILoanDrawdown) => <Link to={`/loans/${loanUuid}/drawdowns/${loanDrawdown.uuid}`}>{drawdownNumber}</Link>,
            title: 'Drawdown Number',
        },
        {
            dataIndex: 'requestedTime',
            defaultSortOrder: 'descend',
            render: (requestedTime: string) => dayjs(requestedTime).format('DD/MM/YYYY'),
            sorter: (a: ILoanDrawdown, b: ILoanDrawdown) => dayjs(a.requestedTime) > dayjs(b.requestedTime) ? 1 : -1,
            title: 'Requested Date',
            width: '20%',
        },
        {
            dataIndex: 'processedTime',
            render: (processedTime: string) => processedTime ? dayjs(processedTime).format('DD/MM/YYYY') : '-',
            title: 'Disbursed Date',
            width: '20%',
        },
        {
            render: (loanDrawdown: ILoanDrawdown) => <LoanDrawdownStatusTag loanDrawdown={loanDrawdown} />,
            title: 'Status',
            width: '10%',
        },
        {
            render: (drawdown: ILoanDrawdown): ReactElement => {
                return (([
                    LoanDrawdownStatusEnum.PendingApproval,
                    LoanDrawdownStatusEnum.Approved,
                    LoanDrawdownStatusEnum.DisbursalInstructed,
                ].includes(drawdown.status) || (drawdown.status === LoanDrawdownStatusEnum.Processed && drawdown.loanFileUuid)) && (
                    <Button
                        href={`${process.env.API_HOST}/loan-drawdowns/${drawdown.uuid}/download-summary`}
                        target='_blank'
                    >
                        Download Summary
                    </Button>
                ));
            },
            title: 'Actions',
            width: '10%',
        },
    ];

    const addModalBlock: JSX.Element = ModalStateEnum.Off !== isAddModalOpen && (
        <DrawdownAddModal
            loan={loan}
            isOpen={ModalStateEnum.Open === isAddModalOpen}
            onCancel={onCloseAddModal}
        />
    );

    const drawdownActions: JSX.Element = _.filter(drawdowns, (drawdown: ILoanDrawdown) => drawdown.status !== LoanDrawdownStatusEnum.Processed).length === 0 && (
        <Space className='actions'>
            <Button onClick={onClickCreate}>Create Drawdown</Button>
        </Space>
    );

    return (
        <Layout uuid={loanUuid} section='drawdowns'>
            {drawdownActions}
            <Typography.Title className='title' level={2}>Drawdowns</Typography.Title>
            <Table
                columns={columns}
                dataSource={_.values(drawdowns)}
                pagination={false}
                rowKey='uuid'
                size='middle'
            />
            {addModalBlock}
        </Layout>
    );
}
